import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
    MessageSquare,
    Target,
    Award,
    CheckCircle,
    Clock,
    TrendingUp,
    Play,
    Eye,
    Loader2,
    RefreshCw,
    Filter,
    ArrowUpDown,
    CheckCircle2,
    XCircle,
    Users,
    Briefcase,
    Handshake,
    Crown,
    Shield,
    Sparkles,
    Star,
    Volume2,
    Mic
} from 'lucide-react';

// Category icons and colors
const CATEGORY_CONFIG = {
    client_mastery: { icon: Briefcase, color: 'text-blue-500', bg: 'bg-blue-500/10', name: 'Client Mastery' },
    candidate_mastery: { icon: Users, color: 'text-emerald-500', bg: 'bg-emerald-500/10', name: 'Candidate Mastery' },
    negotiation_mastery: { icon: Handshake, color: 'text-purple-500', bg: 'bg-purple-500/10', name: 'Negotiation Mastery' },
    executive_communication: { icon: Crown, color: 'text-amber-500', bg: 'bg-amber-500/10', name: 'Executive Communication' },
    compliance_mastery: { icon: Shield, color: 'text-red-500', bg: 'bg-red-500/10', name: 'Compliance Mastery' },
    creative_outreach: { icon: Sparkles, color: 'text-pink-500', bg: 'bg-pink-500/10', name: 'Creative Outreach' }
};

const RolePlayLabAnalytics = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch history and stats
            const [historyRes, statsRes] = await Promise.all([
                api.get('/roleplay/history?limit=50'),
                api.get('/roleplay/stats')
            ]);
            
            setSessions(historyRes.data?.sessions || []);
            setStats(statsRes.data || {});
            
        } catch (error) {
            console.error('Failed to fetch role play data:', error);
            toast.error('Failed to load role play data');
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
        if (score >= 60) return 'text-amber-600 dark:text-amber-400';
        return 'text-red-600 dark:text-red-400';
    };

    const getScoreBadge = (score) => {
        if (score >= 80) return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">Excellent</Badge>;
        if (score >= 60) return <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-300">Good</Badge>;
        return <Badge className="bg-red-500/20 text-red-700 dark:text-red-300">Needs Work</Badge>;
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'completed':
                return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">Completed</Badge>;
            case 'active':
                return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-300">Active</Badge>;
            default:
                return <Badge variant="outline">{status || 'Unknown'}</Badge>;
        }
    };

    const getCategoryInfo = (category) => {
        return CATEGORY_CONFIG[category] || { 
            icon: MessageSquare, 
            color: 'text-gray-500', 
            bg: 'bg-gray-500/10',
            name: category 
        };
    };

    const viewSessionDetail = async (session) => {
        try {
            // Fetch full session details
            const response = await api.get(`/roleplay/sessions/${session.id}`);
            setSelectedSession(response.data);
            setShowDetailModal(true);
        } catch (error) {
            setSelectedSession(session);
            setShowDetailModal(true);
        }
    };

    // Get unique categories from sessions
    const categories = [...new Set(sessions.map(s => s.scenario_category).filter(Boolean))];

    // Sort and filter
    const sortedSessions = [...sessions]
        .filter(s => filterCategory === 'all' || s.scenario_category === filterCategory)
        .filter(s => filterStatus === 'all' || s.status === filterStatus)
        .sort((a, b) => {
            let aVal, bVal;
            switch(sortField) {
                case 'score':
                    aVal = a.overall_score || 0;
                    bVal = b.overall_score || 0;
                    break;
                case 'category':
                    aVal = a.scenario_category || '';
                    bVal = b.scenario_category || '';
                    break;
                case 'status':
                    aVal = a.status || '';
                    bVal = b.status || '';
                    break;
                default:
                    aVal = new Date(a.created_at || 0);
                    bVal = new Date(b.created_at || 0);
            }
            if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
            return aVal < bVal ? 1 : -1;
        });

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    // Calculate summary stats
    const completedSessions = sessions.filter(s => s.status === 'completed');
    const avgScore = completedSessions.length > 0
        ? Math.round(completedSessions.reduce((sum, s) => sum + (s.overall_score || 0), 0) / completedSessions.length)
        : 0;
    const passingCount = completedSessions.filter(s => (s.overall_score || 0) >= 80).length;
    const bestScore = completedSessions.length > 0
        ? Math.max(...completedSessions.map(s => s.overall_score || 0))
        : 0;

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="roleplay-analytics">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Mic className="h-7 w-7 text-primary" />
                            Role Play Lab Analytics
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Track your communication practice sessions and performance
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchData}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                        <Button onClick={() => navigate('/roleplay')}>
                            <Play className="h-4 w-4 mr-2" />
                            New Session
                        </Button>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{sessions.length}</p>
                            <p className="text-xs text-muted-foreground">Total Sessions</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{completedSessions.length}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                        </CardContent>
                    </Card>
                    <Card className="border-emerald-500/30 bg-emerald-500/5">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{passingCount}</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Passed (80+)</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{avgScore}%</p>
                            <p className="text-xs text-muted-foreground">Avg Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{bestScore}%</p>
                            <p className="text-xs text-muted-foreground">Best Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{categories.length}</p>
                            <p className="text-xs text-muted-foreground">Categories</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Category Performance */}
                {stats?.category_stats && Object.keys(stats.category_stats).length > 0 && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Target className="h-5 w-5 text-primary" />
                                Performance by Category
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                {Object.entries(stats.category_stats).map(([category, data]) => {
                                    const config = getCategoryInfo(category);
                                    const Icon = config.icon;
                                    const score = Math.round(data.avg_score || 0);
                                    
                                    return (
                                        <Card key={category} className={score >= 80 ? 'border-emerald-500/30' : ''}>
                                            <CardContent className="p-3">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className={`p-1.5 rounded ${config.bg}`}>
                                                        <Icon className={`h-4 w-4 ${config.color}`} />
                                                    </div>
                                                </div>
                                                <p className="text-xs font-medium truncate">{config.name}</p>
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className={`text-lg font-bold ${getScoreColor(score)}`}>{score}%</span>
                                                    <span className="text-xs text-muted-foreground">{data.count} done</span>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Filter:</span>
                    </div>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map(cat => {
                                const config = getCategoryInfo(cat);
                                return <SelectItem key={cat} value={cat}>{config.name}</SelectItem>;
                            })}
                        </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground ml-auto">
                        Showing {sortedSessions.length} of {sessions.length} sessions
                    </span>
                </div>

                {/* Sessions Table */}
                <Card>
                    <CardContent className="p-0">
                        <ScrollArea className="h-[500px]">
                            <Table>
                                <TableHeader className="sticky top-0 bg-background">
                                    <TableRow>
                                        <TableHead className="w-[200px]">Scenario</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('category')} className="h-8 px-2">
                                                Category
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('status')} className="h-8 px-2">
                                                Status
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('score')} className="h-8 px-2">
                                                Score
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>Difficulty</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('created_at')} className="h-8 px-2">
                                                Date
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedSessions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                {sessions.length === 0 
                                                    ? "No role play sessions yet. Start practicing!"
                                                    : "No sessions match your filters"
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedSessions.map((session) => {
                                            const config = getCategoryInfo(session.scenario_category);
                                            const Icon = config.icon;
                                            const score = session.overall_score || 0;
                                            
                                            return (
                                                <TableRow 
                                                    key={session.id} 
                                                    className="cursor-pointer hover:bg-muted/50"
                                                    onClick={() => viewSessionDetail(session)}
                                                >
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className={`p-1.5 rounded ${config.bg}`}>
                                                                <Icon className={`h-3 w-3 ${config.color}`} />
                                                            </div>
                                                            <span className="text-sm font-medium truncate max-w-[150px]">
                                                                {session.scenario_name || session.scenario_id}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm">{config.name}</span>
                                                    </TableCell>
                                                    <TableCell>{getStatusBadge(session.status)}</TableCell>
                                                    <TableCell>
                                                        <span className={`font-bold ${getScoreColor(score)}`}>
                                                            {score > 0 ? `${Math.round(score)}%` : '-'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="capitalize text-xs">
                                                            {session.difficulty || 'medium'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {session.created_at 
                                                                ? formatDistanceToNow(new Date(session.created_at), { addSuffix: true })
                                                                : '-'
                                                            }
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                viewSessionDetail(session);
                                                            }}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Detail Modal */}
                <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                {selectedSession && (() => {
                                    const config = getCategoryInfo(selectedSession.scenario_category);
                                    const Icon = config.icon;
                                    return (
                                        <>
                                            <div className={`p-2 rounded ${config.bg}`}>
                                                <Icon className={`h-5 w-5 ${config.color}`} />
                                            </div>
                                            {selectedSession.scenario_name || 'Role Play Session'}
                                        </>
                                    );
                                })()}
                            </DialogTitle>
                            <DialogDescription>
                                {selectedSession?.created_at && format(new Date(selectedSession.created_at), 'PPpp')}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedSession && (
                            <div className="space-y-4">
                                {/* Scores */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className={`text-2xl font-bold ${getScoreColor(selectedSession.overall_score || 0)}`}>
                                                {Math.round(selectedSession.overall_score || 0)}%
                                            </p>
                                            <p className="text-xs text-muted-foreground">Overall</p>
                                        </CardContent>
                                    </Card>
                                    {selectedSession.scores?.communication && (
                                        <Card>
                                            <CardContent className="p-3 text-center">
                                                <p className="text-2xl font-bold">{Math.round(selectedSession.scores.communication)}%</p>
                                                <p className="text-xs text-muted-foreground">Communication</p>
                                            </CardContent>
                                        </Card>
                                    )}
                                    {selectedSession.scores?.technique && (
                                        <Card>
                                            <CardContent className="p-3 text-center">
                                                <p className="text-2xl font-bold">{Math.round(selectedSession.scores.technique)}%</p>
                                                <p className="text-xs text-muted-foreground">Technique</p>
                                            </CardContent>
                                        </Card>
                                    )}
                                    {selectedSession.scores?.outcome && (
                                        <Card>
                                            <CardContent className="p-3 text-center">
                                                <p className="text-2xl font-bold">{Math.round(selectedSession.scores.outcome)}%</p>
                                                <p className="text-xs text-muted-foreground">Outcome</p>
                                            </CardContent>
                                        </Card>
                                    )}
                                </div>
                                
                                {/* Details */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Status</span>
                                        {getStatusBadge(selectedSession.status)}
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Difficulty</span>
                                        <Badge variant="outline" className="capitalize">{selectedSession.difficulty || 'medium'}</Badge>
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Mode</span>
                                        <Badge variant="outline">
                                            {selectedSession.voice_mode ? <Volume2 className="h-3 w-3 mr-1 inline" /> : <MessageSquare className="h-3 w-3 mr-1 inline" />}
                                            {selectedSession.voice_mode ? 'Voice' : 'Text'}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Exchanges</span>
                                        <span>{selectedSession.conversation?.length || 0}</span>
                                    </div>
                                </div>
                                
                                {/* Feedback */}
                                {selectedSession.feedback && (
                                    <div className="p-3 rounded-lg bg-muted/50">
                                        <h4 className="font-medium mb-2 flex items-center gap-2">
                                            <Star className="h-4 w-4 text-amber-500" />
                                            Feedback
                                        </h4>
                                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                            {typeof selectedSession.feedback === 'string' 
                                                ? selectedSession.feedback 
                                                : selectedSession.feedback?.summary || 'No feedback available'
                                            }
                                        </p>
                                    </div>
                                )}
                                
                                {/* Actions */}
                                <div className="flex gap-2 pt-2">
                                    <Button 
                                        variant="outline" 
                                        className="flex-1"
                                        onClick={() => setShowDetailModal(false)}
                                    >
                                        Close
                                    </Button>
                                    <Button 
                                        className="flex-1"
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            navigate(`/roleplay?scenario=${selectedSession.scenario_id}`);
                                        }}
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        Try Again
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default RolePlayLabAnalytics;
